/**
 * Little post-capture "moment" — a confetti burst over the canvas plus a
 * short Web-Audio shutter "kchunk". Both are no-ops when the user has
 * `prefers-reduced-motion: reduce` set (motion) or muted the OS volume
 * (we don't fight that). Confetti is lazy-loaded so the booth shell
 * doesn't pay for it on first paint.
 */
const REDUCED_MOTION =
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

let confettiFn: ((opts?: Record<string, unknown>) => void) | null = null;
async function loadConfetti(): Promise<typeof confettiFn> {
  if (confettiFn) return confettiFn;
  try {
    const mod = await import('canvas-confetti');
    confettiFn = mod.default as (opts?: Record<string, unknown>) => void;
    return confettiFn;
  } catch {
    return null;
  }
}

export function celebrateConfetti(): void {
  if (REDUCED_MOTION) return;
  void (async () => {
    const fn = await loadConfetti();
    if (!fn) return;
    // Two staggered bursts from the bottom-left and bottom-right corners
    // angled toward the center — reads as a celebratory pop without
    // hiding the freshly-captured photo.
    fn({
      particleCount: 60,
      spread: 70,
      startVelocity: 45,
      origin: { x: 0.15, y: 0.85 },
      angle: 60,
      ticks: 220,
      scalar: 0.9,
      disableForReducedMotion: true,
    });
    fn({
      particleCount: 60,
      spread: 70,
      startVelocity: 45,
      origin: { x: 0.85, y: 0.85 },
      angle: 120,
      ticks: 220,
      scalar: 0.9,
      disableForReducedMotion: true,
    });
  })();
}

// -- Shutter SFX (Web Audio, no asset needed) --

let audioCtx: AudioContext | null = null;
function getCtx(): AudioContext | null {
  if (audioCtx) return audioCtx;
  const C = (window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext })
    .AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!C) return null;
  audioCtx = new C();
  return audioCtx;
}

export function shutterClick(): void {
  const ctx = getCtx();
  if (!ctx) return;
  // Some browsers leave the AudioContext suspended until a user gesture;
  // we're inside the shutter-button click handler, so a no-op resume is
  // cheap and safe.
  if (ctx.state === 'suspended') void ctx.resume();

  const now = ctx.currentTime;
  // Two-stage shutter: a sharp click (high noise burst) immediately
  // followed by a soft mechanical "thunk" (low triangle oscillator).
  // Both run through a master gain so we can quiet the overall level.
  const master = ctx.createGain();
  master.gain.value = 0.35;
  master.connect(ctx.destination);

  // Click — short white-noise burst, high-passed.
  const noiseBuf = ctx.createBuffer(1, 1024, ctx.sampleRate);
  const noiseData = noiseBuf.getChannelData(0);
  for (let i = 0; i < noiseData.length; i++) {
    noiseData[i] = (Math.random() * 2 - 1) * (1 - i / noiseData.length);
  }
  const noise = ctx.createBufferSource();
  noise.buffer = noiseBuf;
  const hp = ctx.createBiquadFilter();
  hp.type = 'highpass';
  hp.frequency.value = 2200;
  const noiseGain = ctx.createGain();
  noiseGain.gain.setValueAtTime(0.0, now);
  noiseGain.gain.linearRampToValueAtTime(0.7, now + 0.002);
  noiseGain.gain.exponentialRampToValueAtTime(0.001, now + 0.06);
  noise.connect(hp).connect(noiseGain).connect(master);
  noise.start(now);
  noise.stop(now + 0.08);

  // Thunk — low triangle with a fast envelope.
  const osc = ctx.createOscillator();
  osc.type = 'triangle';
  osc.frequency.setValueAtTime(180, now + 0.03);
  osc.frequency.exponentialRampToValueAtTime(60, now + 0.18);
  const oscGain = ctx.createGain();
  oscGain.gain.setValueAtTime(0.0, now + 0.03);
  oscGain.gain.linearRampToValueAtTime(0.5, now + 0.05);
  oscGain.gain.exponentialRampToValueAtTime(0.001, now + 0.22);
  osc.connect(oscGain).connect(master);
  osc.start(now + 0.03);
  osc.stop(now + 0.25);
}
